import { type FC, Fragment } from 'react';
import IconMdi from '@components/IconMdi';
import { Show } from '@components/Show';
import { useEntityStore } from '@ContractBuilder/store';
import { useCommentsUIStore } from '@ContractBuilder/store/comments.store';
import type { ResourceBlock } from '@domain/schemas/contract.schema';
import { isDraftStatus, isReviewStatus } from '@helpers/status';
import { mdiChevronRight } from '@mdi/js';
import { scrollIntoView } from '@root/src/utils/scroll-into-view';
import { cn } from 'cn';
import { v4 as uuidv4 } from 'uuid';

import { useCommentMutations } from '../hooks/useCommentMutations';
import { getCommentBlockId } from '../utils';
import { type CommentThread, isAnchorStale } from '../utils/threads';

import { CommentComposer } from './CommentComposer';
import { CommentThreadView } from './CommentThreadView';

interface BlockCommentsProps {
  block?: ResourceBlock;
  threads: CommentThread[];
  isActive: boolean;
}

/** One block's group in the panel: breadcrumb, optional new-thread composer, then its threads. */
export const BlockComments: FC<BlockCommentsProps> = ({ block, threads, isActive }) => {
  const { submission, isLoading } = useEntityStore(({ submission, isLoading }) => ({ submission, isLoading }));
  const pendingThread = useCommentsUIStore(({ pendingThread }) => pendingThread);
  const activeThreadIds = useCommentsUIStore(({ activeThreadIds }) => activeThreadIds);
  const setActiveThreadIds = useCommentsUIStore(({ setActiveThreadIds }) => setActiveThreadIds);
  const clearPendingThread = useCommentsUIStore(({ clearPendingThread }) => clearPendingThread);
  const setActiveBlockId = useCommentsUIStore(({ setActiveBlockId }) => setActiveBlockId);
  const { addComment } = useCommentMutations();

  const blockSection = submission?.sections?.find((section) => section.blocks.some((b) => b.id === block?.id));
  const isReview = isReviewStatus(submission?.status);
  const canReply = isReview || isDraftStatus(submission?.status);
  const isPendingHere = Boolean(block) && pendingThread?.blockId === block?.id;
  const focusedThreadId = activeThreadIds[0];

  const handleScrollToBlock = () => {
    if (block) {
      setActiveBlockId(block.id);
      scrollIntoView(block.id, { block: 'start' });
    }
  };

  const handleStartThread = async (text: string) => {
    if (!block) {
      return;
    }
    const threadId = uuidv4();
    await addComment.mutateAsync({ content: text, blockId: block.id, threadId, anchor: pendingThread?.anchor });
    clearPendingThread();
    setActiveThreadIds([threadId]);
  };

  const handleFocusThread = (thread: CommentThread) => {
    clearPendingThread();
    setActiveThreadIds([thread.id]);
  };

  if (!block) {
    return null;
  }

  return (
    <div
      className={cn(
        'w-full cursor-pointer border-b border-gray-200',
        !isActive && 'hover:bg-info-50 transition-colors',
      )}
      id={getCommentBlockId(block.id)}
      onClick={handleScrollToBlock}
    >
      <Show when={threads.length > 0 || isActive}>
        <div className="bg-gray-100 px-7 py-1">
          <div className="flex items-center gap-1">
            <span className="truncate text-xs leading-5 font-normal text-gray-400" title={blockSection?.label}>
              {blockSection?.label || 'Untitled Section'}
            </span>
            <IconMdi path={mdiChevronRight} size={0.75} className="text-gray-400" />
            <span className="truncate text-xs leading-5 font-normal text-gray-400" title={block.name}>
              {block.name || 'Untitled Block'}
            </span>
          </div>
        </div>
      </Show>

      <Show when={isPendingHere && isReview}>
        <CommentComposer
          autoFocus
          disabled={isLoading}
          onSubmit={handleStartThread}
          placeholder="Add comment..."
          quote={pendingThread?.anchor?.quote}
          submitLabel="Comment"
        />
      </Show>

      {threads.map((thread, index) => (
        <Fragment key={thread.id}>
          <Show when={index > 0 && !thread.isAnchored && !thread.isDetached}>
            <div className="h-1 w-full bg-gray-100" />
          </Show>
          <CommentThreadView
            block={block}
            canReply={canReply}
            canResolve={isReview}
            disableActions={isLoading}
            isFocused={!isPendingHere && thread.id === focusedThreadId}
            isStale={thread.isAnchored && isAnchorStale(thread.root.anchor!, block.content)}
            onFocus={() => handleFocusThread(thread)}
            thread={thread}
          />
        </Fragment>
      ))}
    </div>
  );
};
